// ==========================================================================
// Pre-Alert Actions Service - Browse and activate PreAlert-level actions
// surfaced even when no weather alert is active.
// ==========================================================================

import api from '@/lib/axios';
import type { UserActionDetail } from '@/types/action.types';

export interface PreAlertActionStep {
  id: number;
  order: number;
  title: string;
  description: string;
  requires_evidence: boolean;
}

export interface PreAlertUserAction {
  id: number;
  status: 'assigned' | 'active' | 'completed' | 'expired';
}

export interface PreAlertAction {
  id: number;
  kb_id: string;
  name: string;
  description: string;
  behavior_statement?: string;
  incentive_appropriate: boolean;
  time_effort_minutes: number | null;
  /**
   * Present when the user has a non-expired pre-alert UserAction for this
   * action — the FE swaps the CTA from "Start this action" to "Continue Action".
   */
  user_action?: PreAlertUserAction | null;
  steps?: PreAlertActionStep[];
}

interface ListResponse {
  actions: PreAlertAction[];
}

interface ActivateResponse {
  action: UserActionDetail;
}

/** List up to 3 PreAlert actions surfaced to the user. Empty when alert is active. */
export async function getPreAlertActions(): Promise<PreAlertAction[]> {
  const response = await api.get<ListResponse>('/user/pre-alert-actions');
  return response.data.actions;
}

/** Read-only preview of a single PreAlert action with steps. */
export async function getPreAlertActionDetail(actionId: number): Promise<PreAlertAction> {
  const response = await api.get<PreAlertAction>(`/user/pre-alert-actions/${actionId}`);
  return response.data;
}

/** Create a UserAction (status=active, no alert link) and return it. */
export async function activatePreAlertAction(actionId: number): Promise<UserActionDetail> {
  const response = await api.post<ActivateResponse>(`/user/pre-alert-actions/${actionId}/activate`);
  return response.data.action;
}
